import {
  AdminActivity, AdminSession, EmailDelivery, EmailQuota, NewRegistration, UsageItem, UsageMetric, Registration, RegistrationFilters, RegistrationResult,
  RegistrationUpdate, SendProgress, SendRequest, Webinar, WebinarInput,
} from "./types";
import { Backend, WEBINAR_BACKEND } from "./config";
import { createIndexedDbRepository } from "./indexedDbRepository";
import { createSupabaseRepository } from "./supabaseRepository";

/** Spam signals the server checks: the hidden honeypot field and how long the form was open */
export interface SubmissionMeta {
  website: string;
  elapsedMs: number;
}

/**
 * Everything the webinar pages and the admin dashboard need from storage. UI code only talks
 * to this interface. Two implementations:
 *   prototype — IndexedDB in this browser (indexedDbRepository.ts); nothing leaves the device
 *   supabase  — the live database and Edge Functions (supabaseRepository.ts)
 * Which one runs is set by REACT_APP_WEBINAR_BACKEND (config.ts).
 */
export interface WebinarRepository {
  readonly backend: Backend;

  // ── Public pages ──
  /** Every webinar visitors may see (never drafts), soonest first */
  listPublicWebinars(): Promise<Webinar[]>;
  getPublicWebinar(slug: string): Promise<Webinar | undefined>;
  /** Registrations that aren't cancelled, per webinar ID: totals only, for seat counts */
  countActiveRegistrations(): Promise<Record<string, number>>;
  /** Validated again on the server, which checks duplicates, capacity and deadlines. Throws RegistrationError */
  register(data: NewRegistration, meta: SubmissionMeta): Promise<RegistrationResult>;

  // ── Admin dashboard. In production the database refuses all of these to non-administrators ──
  /** null when signed out */
  getAdminSession(): Promise<AdminSession | null>;
  /** Throws AdminError sign_in_failed or rate_limited */
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  /** Including drafts */
  listAllWebinars(): Promise<Webinar[]>;
  /**
   * Adds a webinar (no id) or updates one. Webinars aren't deleted: registrations stay attached,
   * so cancel or return to draft instead. Throws AdminError duplicate (web address in use) or invalid
   */
  saveWebinar(input: WebinarInput, id?: string): Promise<Webinar>;
  listRegistrations(filters?: RegistrationFilters): Promise<Registration[]>;
  /** Throws AdminError duplicate when restoring would create a second active registration */
  updateRegistration(id: string, data: RegistrationUpdate): Promise<void>;
  listEmailDeliveries(): Promise<EmailDelivery[]>;
  /** Private joining links, by webinar ID */
  getMeetingLinks(): Promise<Record<string, string>>;
  /** An empty link removes it */
  saveMeetingLink(webinarId: string, link: string): Promise<void>;
  /** Queues and sends joining instructions, reporting after each batch. Throws AdminError no_meeting_link */
  sendJoiningInstructions(request: SendRequest, onProgress?: (progress: SendProgress) => void): Promise<SendProgress>;
  /** Re-sends failed emails (for one webinar, or all when webinarId is null) */
  retryFailedEmails(webinarId: string | null, onProgress?: (progress: SendProgress) => void): Promise<SendProgress>;
  /** Sends emails already waiting in the queue, e.g. held back by the daily limit (one webinar, or all) */
  sendWaitingEmails(webinarId: string | null, onProgress?: (progress: SendProgress) => void): Promise<SendProgress>;
  /** Today's position against the daily email limit */
  getEmailQuota(): Promise<EmailQuota>;
  /** The Supabase project against its plan's limits; null in the prototype, which has no project */
  getProjectUsage(): Promise<UsageItem[] | null>;
  /** Records a figure copied from Supabase's usage page (egress, log ingestion, log query) */
  recordProjectUsage(metric: UsageMetric, value: number): Promise<void>;
  /** The audit log, newest first; null in the prototype, which keeps none */
  listAdminActivity(limit: number): Promise<AdminActivity[] | null>;

  /** Runs after any change made through this repository (and, in the prototype, in other tabs) */
  subscribe(listener: () => void): () => void;
}

/** Prototype-only conveniences for reviewing the demo. Not part of the production interface. */
export interface PrototypeTools {
  resetToDemoData(): Promise<void>;
  /** Last-resort recovery: deletes this browser's prototype database so it's rebuilt on reload */
  deleteLocalDatabase(): Promise<void>;
  /** Adds clearly fictional attendees to the demo webinars; returns how many were added */
  addSampleRegistrations(): Promise<number>;
}

const prototype = WEBINAR_BACKEND === "prototype" ? createIndexedDbRepository() : null;

export const webinarRepository: WebinarRepository = prototype ?? createSupabaseRepository();

/** null in production */
export const prototypeTools: PrototypeTools | null = prototype;
